import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

export interface AttributionLink {
    label: string;
    url: string;
}

export interface MapStyle {
    id: string;
    name: string;
    url: string;
    attribution: string;
    attributions: AttributionLink[];
}

export interface ColorScheme {
    id: string;
    name: string;
    filter: string;
}

export const MAP_STYLES: MapStyle[] = [
    {
        id: 'standard',
        name: 'Standard',
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '© OpenStreetMap contributors',
        attributions: [
            { label: '© OpenStreetMap contributors', url: 'https://www.openstreetmap.org/copyright' }
        ]
    },
    {
        id: 'terrain',
        name: 'Terrain',
        url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
        attribution: '© OpenTopoMap contributors',
        attributions: [
            { label: '© OpenTopoMap contributors', url: 'https://opentopomap.org' },
            { label: '© OpenStreetMap contributors', url: 'https://www.openstreetmap.org/copyright' }
        ]
    },
    {
        id: 'satellite',
        name: 'Satellite',
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        attribution: '© Esri',
        attributions: [
            { label: '© Esri', url: 'https://www.esri.com/' },
            { label: '© OpenStreetMap contributors', url: 'https://www.openstreetmap.org/copyright' }
        ]
    },
    {
        id: 'watercolor',
        name: 'Watercolor',
        url: `https://tiles.stadiamaps.com/tiles/stamen_watercolor/{z}/{x}/{y}.jpg?api_key=${import.meta.env?.STADIA_MAPS_API_KEY || ''}`,
        attribution: '© Stamen Design, © Stadia Maps',
        attributions: [
            { label: '© Stamen Design', url: 'https://stamen.com/' },
            { label: '© Stadia Maps', url: 'https://stadiamaps.com/' },
            { label: '© OpenStreetMap contributors', url: 'https://www.openstreetmap.org/copyright' }
        ]
    }
];

export const COLOR_SCHEMES: ColorScheme[] = [
    {
        id: 'default',
        name: 'Default',
        filter: 'none'
    },
    {
        id: 'parchment',
        name: 'Parchment',
        filter: 'sepia(30%) saturate(70%)'
    },
    {
        id: 'ancient',
        name: 'Ancient',
        filter: 'sepia(40%) saturate(60%) contrast(110%)'
    },
    {
        id: 'ancient-map',
        name: 'Ancient Map (Heavy)',
        filter: 'sepia(60%) saturate(40%) contrast(95%) brightness(105%) blur(0.3px)'
    },
    {
        id: 'aged-parchment',
        name: 'Aged Parchment',
        filter: 'sepia(70%) saturate(30%) contrast(90%) brightness(110%) hue-rotate(10deg)'
    },
    {
        id: 'faded-historical',
        name: 'Faded Historical',
        filter: 'grayscale(30%) sepia(50%) saturate(50%) contrast(85%) brightness(108%)'
    },
    {
        id: 'night',
        name: 'Night',
        filter: 'invert(100%) hue-rotate(180deg) brightness(90%)'
    },
    {
        id: 'high-contrast',
        name: 'High Contrast',
        filter: 'contrast(120%) saturate(80%)'
    }
];

interface MapStyleControlProps {
    selectedStyle: string;
    onStyleChange: (styleId: string) => void;
    showLabels: boolean;
    onShowLabelsChange: (show: boolean) => void;
    showAncientRoads: boolean;
    onShowAncientRoadsChange: (show: boolean) => void;
    showSeaLanes: boolean;
    onShowSeaLanesChange: (show: boolean) => void;
    showRomanProvinces?: boolean;
    onShowRomanProvincesChange?: (show: boolean) => void;
    showAncientEmpires?: boolean;
    onShowAncientEmpiresChange?: (show: boolean) => void;
    passageEventsOnly?: boolean;
    onPassageEventsOnlyChange?: (show: boolean) => void;
    className?: string;
}

export function MapStyleControl({
    selectedStyle,
    onStyleChange,
    showLabels,
    onShowLabelsChange,
    showAncientRoads,
    onShowAncientRoadsChange,
    showSeaLanes,
    onShowSeaLanesChange,
    showRomanProvinces,
    onShowRomanProvincesChange,
    showAncientEmpires,
    onShowAncientEmpiresChange,
    passageEventsOnly,
    onPassageEventsOnlyChange,
    className = ''
}: MapStyleControlProps) {
    return (
        <div className={`space-y-4 ${className}`}>
            {/* Tile Layer Selection */}
            <div className="flex items-center gap-3">
                <Select value={selectedStyle} onValueChange={onStyleChange}>
                    <SelectTrigger id="map-style" className="flex-1">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {MAP_STYLES.map((style) => (
                            <SelectItem key={style.id} value={style.id}>
                                {style.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Display Options */}
            <div className="space-y-3">
                {/* Only show "Passage Events Only" checkbox if handlers are provided (passage-specific) */}
                {passageEventsOnly !== undefined && onPassageEventsOnlyChange && (
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="passage-events-only"
                            checked={passageEventsOnly}
                            onCheckedChange={(checked) => onPassageEventsOnlyChange(checked === true)}
                        />
                        <label
                            htmlFor="passage-events-only"
                            className="text-sm cursor-pointer"
                        >
                            Passage Places Only
                        </label>
                    </div>
                )}

                {/* <div className="flex items-center gap-2">
                    <Checkbox
                        id="show-labels"
                        checked={showLabels}
                        onCheckedChange={(checked) => onShowLabelsChange(checked === true)}
                    />
                    <label
                        htmlFor="show-labels"
                        className="text-sm cursor-pointer"
                    >
                        Show ancient place labels
                    </label>
                </div> */}

                <div className="flex items-center gap-2">
                    <Checkbox
                        id="show-ancient-roads"
                        checked={showAncientRoads}
                        onCheckedChange={(checked) => onShowAncientRoadsChange(checked === true)}
                    />
                    <label
                        htmlFor="show-ancient-roads"
                        className="text-sm cursor-pointer"
                    >
                        Show ancient roads
                    </label>
                </div>

                <div className="flex items-center gap-2">
                    <Checkbox
                        id="show-sea-lanes"
                        checked={showSeaLanes}
                        onCheckedChange={(checked) => onShowSeaLanesChange(checked === true)}
                    />
                    <label
                        htmlFor="show-sea-lanes"
                        className="text-sm cursor-pointer"
                    >
                        Show ancient sea lanes
                    </label>
                </div>

                {/* <div className="flex items-center gap-2">
                    <Checkbox
                        id="show-roman-provinces"
                        checked={showRomanProvinces}
                        onCheckedChange={(checked) => onShowRomanProvincesChange(checked === true)}
                    />
                    <label
                        htmlFor="show-roman-provinces"
                        className="text-sm cursor-pointer"
                    >
                        Show Roman provinces
                    </label>
                </div> */}

                {/* <div className="flex items-center gap-2">
                    <Checkbox
                        id="show-ancient-empires"
                        checked={showAncientEmpires}
                        onCheckedChange={(checked) => onShowAncientEmpiresChange(checked === true)}
                    />
                    <label
                        htmlFor="show-ancient-empires"
                        className="text-sm cursor-pointer"
                    >
                        Show ancient empires
                    </label>
                </div> */}
            </div>
        </div>
    );
}
